import { useMemo } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { InviteBoardStatus } from "@graftus/schema";
import { StepHeading } from "../OnboardingShell";
import { LinkButton, PrimaryButton } from "../ui/buttons";
import { RemindersList, StatCard, StatusList, TeamProfileBars, type StatusRow, type TeamBar } from "../ui/status-board";

export interface DoneInvite {
  sessionId: string;
  memberId: string;
  memberName: string | null;
  salesTeamId: string | null;
  boardStatus: InviteBoardStatus;
}

interface DoneScreenProps {
  mode: "sent" | "scheduled";
  scheduledLabel: string | null;
  invites: DoneInvite[];
  teams: Array<{ id: string; name: string }>;
  remindersEnabled: boolean;
  /** Roster-based readiness from the server; the invite-derived bars are the fallback. */
  readiness?: TeamBar[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onGoToApp: () => void;
}

/** The last screen: stat cards, status per employee, team profile bars and the reminder plan. */
export function DoneScreen({ mode, scheduledLabel, invites, teams, remindersEnabled, readiness, loading, error, onRetry, onGoToApp }: DoneScreenProps) {
  const { t } = useTranslation("onboarding");
  const teamName = useMemo(() => new Map(teams.map((team) => [team.id, team.name])), [teams]);

  const count = (status: InviteBoardStatus) => invites.filter((i) => i.boardStatus === status).length;
  const rows: StatusRow[] = invites.map((invite) => ({
    id: invite.sessionId,
    name: invite.memberName ?? invite.memberId,
    team: invite.salesTeamId ? (teamName.get(invite.salesTeamId) ?? null) : null,
    status: invite.boardStatus,
  }));

  // Readiness among those invited, until the server's roster-based figure is loaded.
  const bars: TeamBar[] = useMemo(() => {
    if (readiness && readiness.length > 0) return readiness;
    const byTeam = new Map<string, TeamBar>();
    for (const invite of invites) {
      if (!invite.salesTeamId) continue;
      const bar = byTeam.get(invite.salesTeamId) ?? {
        id: invite.salesTeamId,
        name: teamName.get(invite.salesTeamId) ?? invite.salesTeamId,
        done: 0,
        total: 0,
      };
      bar.total += 1;
      if (invite.boardStatus === "done") bar.done += 1;
      byTeam.set(invite.salesTeamId, bar);
    }
    return Array.from(byTeam.values());
  }, [invites, teamName, readiness]);

  const stats =
    mode === "sent"
      ? [
          { label: t("done.stats.done"), value: count("done"), tone: "success" as const },
          { label: t("done.stats.started"), value: count("started"), tone: "primary" as const },
          { label: t("done.stats.opened"), value: count("opened"), tone: "info" as const },
          { label: t("done.stats.notOpened"), value: count("invited"), tone: "muted" as const },
        ]
      : [
          { label: t("done.stats.scheduled"), value: count("scheduled"), tone: "warning" as const },
          { label: t("done.stats.teams"), value: bars.length, tone: "primary" as const },
          { label: t("done.stats.reminders"), value: remindersEnabled ? 2 : 0, tone: "info" as const },
          { label: t("done.stats.done"), value: count("done"), tone: "success" as const },
        ];

  return (
    <div data-testid="onboarding-done">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-g-pill bg-g-success-tint text-g-success" aria-hidden="true">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <StepHeading
        title={t(`done.${mode}.title`)}
        lead={
          mode === "sent"
            ? t("done.sent.sub", { n: invites.length })
            : t("done.scheduled.sub", { n: invites.length, slot: scheduledLabel ?? "" })
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <section className="mt-8">
        <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wide text-g-ink-500">{t("done.statusTitle")}</h2>
        {loading && invites.length === 0 ? (
          <div className="flex items-center gap-2 text-[14px] text-g-ink-500" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t("meta.saving")}
          </div>
        ) : error ? (
          <p className="flex items-center gap-3 rounded-g-md bg-g-danger-tint px-4 py-3 text-[14px] text-g-danger" role="alert">
            {t("done.loadError")}
            <LinkButton onClick={onRetry}>{t("done.retry")}</LinkButton>
          </p>
        ) : rows.length === 0 ? (
          <p className="rounded-g-md bg-g-bg-input px-4 py-3 text-[14px] text-g-ink-500">{t("done.empty")}</p>
        ) : (
          <StatusList rows={rows} />
        )}
      </section>

      {bars.length > 0 ? (
        <section className="mt-8">
          <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wide text-g-ink-500">{t("done.teamsTitle")}</h2>
          <TeamProfileBars teams={bars} />
          <p className="mt-2 text-[12.5px] text-g-ink-500">{t("done.teamsNote")}</p>
        </section>
      ) : null}

      <section className="mt-8">
        <h2 className="mb-3 text-[13px] font-bold uppercase tracking-wide text-g-ink-500">{t("done.remindersTitle")}</h2>
        <RemindersList enabled={remindersEnabled} mode={mode} />
      </section>

      <div className="mt-8">
        <PrimaryButton onClick={onGoToApp}>{t("done.cta")}</PrimaryButton>
      </div>
    </div>
  );
}
